export const billingConfiguration = {
  header: {
    title: "Configuração de faturamento",
    description:
      "Gerencie os emissores de faturamento da sua conta e vincule cada anúncio ao emissor correto.",
  },

  intro: {
    lead:
      "Um <strong>emissor de faturamento</strong> é a entidade fiscal que emite faturas para seus hóspedes. Você pode ter vários e escolher qual emite faturas para cada anúncio.",
    feats: {
      fiscalData: {
        label: "Dados fiscais próprios",
        tip: "Razão social, NIF/CIF e endereço fiscal exibidos na fatura.",
      },
      series: {
        label: "Séries independentes",
        tip: "Cada emissor mantém sua própria série e sequência (e para faturas retificativas), nunca misturadas.",
      },
      verifactu: {
        label: "VeriFactu por emissor",
        tip: "Cada emissor cumpre o VeriFactu (assinatura e registro na AEAT) separadamente, conforme sua configuração fiscal.",
      },
      assignment: {
        label: "Atribuição por anúncio",
        tip: "Vincule cada anúncio a um emissor. Os não atribuídos usam o emissor principal.",
      },
    },
    foot: "Ao emitir, é usado o emissor do anúncio da reserva (no check-out ou quando o hóspede solicita ao agente de IA).",
  },

  issuers: {
    card: {
      title: "Emissores de faturamento",
      subtitle: "Crie vários emissores e atribua seus anúncios a cada um.",
    },
    searchPlaceholder: "Buscar emissor por nome ou NIF…",
    newButton: "Novo emissor",
    noName: "Sem nome",
    defaultBadge: "Principal",
    listingsCount: "{count} anúncios",
    empty: {
      noMatch: "Nenhum emissor corresponde à sua busca.",
      none: "Você ainda não tem emissores. Crie o primeiro com “Novo emissor”.",
    },
  },

  assign: {
    card: {
      title: "Atribuição de anúncios",
      subtitle:
        "Escolha qual emissor emite faturas para cada anúncio. Um anúncio pode ter apenas um emissor; os não atribuídos usam o principal.",
    },
    searchPlaceholder: "Buscar anúncio…",
    filter: {
      all: "Todos",
      unassigned: "Não atribuídos",
    },
    issuerFallback: "Emissor nº {id}",
    principalSuffix: " (principal)",
    unassignedOption: "— Não atribuído (principal)",
    empty: {
      noMatch: "Nenhum anúncio corresponde ao filtro.",
      none: "Você ainda não tem anúncios.",
    },
    count: "{shown} de {total} anúncios",
  },

  drawer: {
    editTitle: "Editar emissor",
    newTitle: "Novo emissor",
    subtitle: "Dados fiscais do emissor de faturamento",
    closeAria: "Fechar",
    footer: {
      isDefault: "É o principal",
      setDefault: "Definir como principal",
      delete: "Excluir",
      cancel: "Cancelar",
      saving: "Salvando...",
      save: "Salvar emissor",
    },
  },

  company: {
    title: "Dados da empresa",
    subtitle: "Informações fiscais da sua propriedade",
    country: {
      label: "País de faturamento",
      hint: "Escolha o país fiscal que se aplica a esta configuração.",
    },
    personType: {
      label: "Tipo de entidade",
      company: "Pessoa jurídica (empresa)",
      individual: "Pessoa física (autônomo)",
    },
    legalName: {
      label: "Nome / Razão social",
      placeholderCompany: "ex.: INNDEV S.L.",
      placeholderIndividual: "ex.: María García",
      hint: "Nome fiscal da empresa ou pessoa física",
    },
    co: {
      regime: {
        label: "Regime DIAN",
        common: "Regime comum",
        simplified: "Regime simplificado",
        hint: "Necessário para o faturamento eletrônico da DIAN",
      },
      municipality: {
        label: "Município DIAN",
        hint: "Código DIVIPOLA do seu município — exigido pela DIAN.",
        selected: "Selecionado: {code}",
      },
      responsibilities: {
        label: "Responsabilidades fiscais",
        placeholder: "ex.: O-13, R-99-PN",
        hint: "Códigos separados por vírgula. Mais comum: <strong>O-13</strong>",
      },
    },
    mx: {
      regime: {
        label: "Regime fiscal (SAT)",
        placeholder: "ex.: 601",
        hint: "Código de regime fiscal do SAT",
      },
      zip: {
        label: "Código postal fiscal (SAT)",
        placeholder: "ex.: 06600",
        hint: "Código postal fiscal do SAT",
      },
    },
  },

  address: {
    title: "Endereço fiscal",
    subtitle: "O endereço exibido nas faturas",
    street: {
      label: "Rua e número",
    },
    city: {
      label: "Cidade",
      hint: "Cidade e código postal",
    },
    email: {
      label: "E-mail de faturamento",
      placeholder: "billing@yourproperty.com",
    },
    phone: {
      label: "Telefone",
    },
    ph: {
      ES: {
        street: "ex.: Gran Vía 42, 3º andar",
        city: "Madri",
        zip: "28001",
        regionLabel: "Província",
        region: "Madri",
        phone: "+34 123 456 789",
      },
      CO: {
        street: "ex.: Calle 13 No. 10-25",
        city: "Bogotá",
        zip: "110111",
        regionLabel: "Departamento",
        region: "Cundinamarca",
        phone: "+57 601 234 5678",
      },
      MX: {
        street: "ex.: Av. Insurgentes Sur 1234",
        city: "Cidade do México",
        zip: "06600",
        regionLabel: "Estado",
        region: "CDMX",
        phone: "+52 55 1234 5678",
      },
    },
  },

  bank: {
    title: "Conta para receber",
    subtitle: "Onde lhe pagam quando a forma de pagamento é transferência",
    iban: {
      label: "Número de conta (IBAN)",
      placeholder: "PT50 0002 0123 1234 5678 9015 4",
      hint: "Aparece impresso na fatura apenas quando a forma de pagamento é transferência.",
      error: "O número de conta (IBAN) não tem um formato válido.",
    },
    bic: {
      label: "BIC / SWIFT",
      placeholder: "BCOMPTPL",
      hint: "Opcional. É necessário para pagamentos de fora da zona SEPA.",
    },
    holder: {
      label: "Titular da conta",
      placeholder: "Deixe em branco se coincidir com o nome fiscal",
      hint: "Apenas se a conta estiver num nome diferente do da empresa.",
    },
  },

  invoice: {
    title: "Configuração da fatura",
    subtitle: "Formato, moeda e numeração dos seus documentos",
    currency: {
      label: "Moeda padrão",
      hint: "Moeda em que as faturas serão emitidas",
    },
    taxRate: {
      label: "IVA padrão %",
      hintCO: "IVA geral Colômbia: <strong>19%</strong>",
      hintMX: "IVA geral México: <strong>16%</strong>",
      hintES: "IVA reduzido de hotelaria Espanha: <strong>10%</strong> — geral: <strong>21%</strong>",
    },
    type: {
      label: "Tipo de fatura",
      detailed: {
        label: "Detalhada",
        desc: "Cada serviço em sua própria linha",
      },
      simplified: {
        label: "Simplificada",
        desc: "Recibo ou fatura com itens agrupados",
      },
    },
    numbering: {
      label: "Numeração de documentos",
      example: "ex.: {value}",
      invoices: {
        title: "Faturas",
      },
      series: "Série",
      optional: "(opcional)",
      optionalPlaceholder: "Opcional",
      digits: "Dígitos",
      nextNumber: "Próximo número",
      hintOptional: "A série é opcional. Se deixada em branco, a fatura será emitida apenas com o número sequencial.",
      hintFormat: "Se você definir uma série, o sistema montará o código como SÉRIE-NÚMERO. Sem série, produzirá apenas NÚMERO.",
      hintWarn: "Defina este valor antes de emitir a primeira fatura da série para manter a sequência fiscal alinhada.",
      hintSandboxCO: "No sandbox use <strong>SETP</strong> — em produção deve corresponder à sua resolução da DIAN.",
    },
    creditNotes: {
      title: "Notas de crédito",
      badge: "Cancelamentos / correções",
      hint: "Usado para a próxima fatura retificativa ou cancelamento que você emitir.",
    },
    footer: {
      label: "Rodapé padrão",
      hint: "Texto que aparecerá no rodapé de todas as faturas",
      phES: "ex.: Operação sujeita ao IVA reduzido de hotelaria conforme art. 91 da Lei do IVA",
      phCO: "ex.: Resolução DIAN nº 000123 de 01-01-2025. Faixas de 1 a 5000000",
      phMX: "ex.: CFDI emitido conforme os requisitos do artigo 29-A do CFF",
    },
  },

  advanced: {
    title: "Opções avançadas",
    subtitle: "Configurações adicionais de envio",
    attachPdf: {
      label: "Anexar PDF",
      text: "Inclua o documento PDF junto com o link de download",
    },
  },

  loading: {
    default: "Carregando...",
    config: "Carregando configuração...",
    profiles: "Carregando emissores...",
    saving: "Salvando configuração...",
  },

  countries: {
    ES: "Espanha",
    CO: "Colômbia",
    MX: "México",
  },

  currencies: {
    EUR: "Euro",
    COP: "Peso colombiano",
    USD: "Dólar americano",
    MXN: "Peso mexicano",
  },

  taxId: {
    labelFallback: "Identificação fiscal",
    helpES: "NIF para pessoas físicas, CIF para empresas. ex.: B12345678",
    helpCO: "NIT com dígito verificador. ex.: 9014514812 (10 dígitos)",
    helpMX: "RFC de 12 ou 13 caracteres. ex.: XAXX010101000",
    helpFallback: "Identificador fiscal",
  },

  validation: {
    taxIdES: "Formato inválido. ex.: B12345678",
    taxIdCO: "O NIT deve ter 9 ou 10 dígitos. ex.: 9014514812",
    taxIdMX: "Formato de RFC inválido. ex.: XAXX010101000",
  },

  msg: {
    reassignError: "Não foi possível reatribuir o anúncio.",
    defaultUpdated: "Emissor principal atualizado.",
    setDefaultError: "Não foi possível definir como principal.",
    deleteConfirm: "Excluir este emissor de faturamento?",
    deleted: "Emissor excluído.",
    deleteError: "Não foi possível excluir o emissor.",
    requiredFields: "Preencha todos os campos obrigatórios antes de salvar.",
    fixTaxId: "Corrija o identificador fiscal antes de salvar.",
    coRegimeRequired: "O regime DIAN é obrigatório para a Colômbia.",
    coMunicipalityRequired: "O município DIAN é obrigatório para a Colômbia.",
    savedOk: "Emissor salvo com sucesso.",
    saveError: "Erro ao salvar a configuração.",
  },

  misc: {
    thisIssuer: "este emissor",
  },
};
